import { minus_circle_disabled, minus_circle_enabled, plus_circle_disabled, plus_circle_enabled } from '@assets/icons';
import { Checkbox, ChipXButton, Text, Tooltip } from '..';
import { CharacterNumberInputType, styleConstant } from '@constant';
import { Button, Popover } from '@mui/material';
import { colorSet } from '@theme';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDownGray400Icon } from '@assets/iconComponents';

interface CharacterNumberInputProps {
  disabled?: boolean;
  needTooltip?: boolean;
  tooltipText?: string;
  characterNumber: {
    maleNumber?: number | string;
    femaleNumber?: number | string;
    wholeGenderNumber?: number | string;
    isWholeGenderNumberChecked?: boolean;
  } | null;
  onApplyClick: (values: {
    maleNumber?: number | string;
    femaleNumber?: number | string;
    wholeGenderNumber?: number | string;
    isWholeGenderNumberChecked?: boolean;
  }) => void;
  onChipXButtonClick?: () => void;
  isError?: boolean;
  isWholeGenderNumberOnly?: boolean;
  isGenderNumbersOnly?: boolean;
  type: CharacterNumberInputType;
}

// CharacterNumberInput: 검색시 이용하는 인원수 인풋 / 남녀 합 3 초과 불가
// AdminCharacterNumberInput: 이미지 업로드/정보수정시 이용하는 인원수 인풋 / 남녀 합 제한 없음
export const CharacterNumberInput = (props: CharacterNumberInputProps) => {
  const {
    disabled = false,
    needTooltip = false,
    tooltipText = '',
    characterNumber,
    onApplyClick,
    onChipXButtonClick,
    isError = false,
    isWholeGenderNumberOnly = false,
    isGenderNumbersOnly = false,
    type
  } = props;

  const [isSelectPaperOpen, setIsSelectPaperOpen] = useState<boolean>(false);

  const [displayText, setDisplayText] = useState<string | null>(
    !characterNumber
      ? null
      : characterNumber.isWholeGenderNumberChecked
        ? `성별 무관 ${characterNumber.wholeGenderNumber}명`
        : `남자 ${characterNumber.maleNumber}명 ∙ 여자 ${characterNumber.femaleNumber}명`
  );
  const selectRef = useRef<HTMLDivElement>(null);

  const [initialCharacterNumber, setInitialCharacterNumber] = useState<{
    maleNumber: number | string;
    femaleNumber: number | string;
    wholeGenderNumber: number | string;
    isWholeGenderNumberChecked: boolean;
  }>({
    maleNumber: characterNumber?.maleNumber ?? 0,
    femaleNumber: characterNumber?.femaleNumber ?? 0,
    wholeGenderNumber: characterNumber?.wholeGenderNumber ?? 0,
    isWholeGenderNumberChecked: characterNumber?.isWholeGenderNumberChecked ?? false
  });

  const [tempCharacterNumber, setTempCharacterNumber] = useState<{
    maleNumber: number | string;
    femaleNumber: number | string;
    wholeGenderNumber: number | string;
    isWholeGenderNumberChecked: boolean;
  }>({
    maleNumber: characterNumber?.maleNumber ?? 0,
    femaleNumber: characterNumber?.femaleNumber ?? 0,
    wholeGenderNumber: characterNumber?.wholeGenderNumber ?? 0,
    isWholeGenderNumberChecked: characterNumber?.isWholeGenderNumberChecked ?? false
  });

  const characterNumberLimit = 3;

  const getMaleNumberMenuItems = useCallback(() => {
    let maleNumberLimit = 3;

    if (!tempCharacterNumber) {
      maleNumberLimit = characterNumberLimit;
    } else if (tempCharacterNumber?.femaleNumber !== '4+') {
      maleNumberLimit = characterNumberLimit - Number(tempCharacterNumber?.femaleNumber);
    } else {
      maleNumberLimit = characterNumberLimit;
    }

    const maleNumberMenuItems = [];

    let i = 0;

    while (i <= maleNumberLimit) {
      maleNumberMenuItems.push(i);
      i++;
    }

    maleNumberMenuItems.push('4+');

    return maleNumberMenuItems;
  }, [tempCharacterNumber?.femaleNumber]);

  const getFemaleNumberMenuItems = useCallback(() => {
    let femaleNumberLimit = 3;

    if (!tempCharacterNumber) {
      femaleNumberLimit = characterNumberLimit;
    } else if (tempCharacterNumber?.maleNumber !== '4+') {
      femaleNumberLimit = characterNumberLimit - Number(tempCharacterNumber?.maleNumber);
    } else {
      femaleNumberLimit = characterNumberLimit;
    }

    const femaleNumberMenuItems = [];

    let i = 0;

    while (i <= femaleNumberLimit) {
      femaleNumberMenuItems.push(i);
      i++;
    }

    femaleNumberMenuItems.push('4+');

    return femaleNumberMenuItems;
  }, [tempCharacterNumber?.maleNumber]);

  const maleNumberOptions = getMaleNumberMenuItems ? getMaleNumberMenuItems() : [];
  const femaleNumberOptions = getFemaleNumberMenuItems ? getFemaleNumberMenuItems() : [];
  const wholeGenderOptions = isWholeGenderNumberOnly ? [1, 2, 3, '4+'] : [0, 1, 2, 3, '4+'];

  const handleNumberChange = (
    type: 'male' | 'female' | 'whole',
    action: 'increase' | 'decrease',
    options: (number | string)[]
  ) => {
    const currentValue =
      type === 'male'
        ? tempCharacterNumber?.maleNumber
        : type === 'female'
          ? tempCharacterNumber?.femaleNumber
          : tempCharacterNumber?.wholeGenderNumber;

    const currentIndex = options.indexOf(currentValue ?? 0);
    if (action === 'increase' && currentIndex < options.length - 1) {
      const newValue = options[currentIndex + 1];
      setTempCharacterNumber((prev) => ({
        ...prev,
        [type === 'male' ? 'maleNumber' : type === 'female' ? 'femaleNumber' : 'wholeGenderNumber']: newValue
      }));
    } else if (action === 'decrease' && currentIndex > 0) {
      const newValue = options[currentIndex - 1];
      setTempCharacterNumber((prev) => ({
        ...prev,
        [type === 'male' ? 'maleNumber' : type === 'female' ? 'femaleNumber' : 'wholeGenderNumber']: newValue
      }));
    }
  };

  const isApplyEnabled = () => {
    if (!initialCharacterNumber.isWholeGenderNumberChecked && !tempCharacterNumber?.isWholeGenderNumberChecked) {
      // 케이스 (1): 마지막 state와 현재 모두 false
      const hasChanged =
        tempCharacterNumber?.maleNumber !== initialCharacterNumber.maleNumber ||
        tempCharacterNumber?.femaleNumber !== initialCharacterNumber.femaleNumber;
      const hasValidSum =
        tempCharacterNumber?.maleNumber === '4+' ||
        tempCharacterNumber?.femaleNumber === '4+' ||
        (typeof tempCharacterNumber?.maleNumber === 'number' &&
          typeof tempCharacterNumber?.femaleNumber === 'number' &&
          tempCharacterNumber?.maleNumber + tempCharacterNumber?.femaleNumber >= 1);

      return hasChanged && hasValidSum;
    }

    if (!initialCharacterNumber.isWholeGenderNumberChecked && tempCharacterNumber?.isWholeGenderNumberChecked) {
      // 케이스 (2): false에서 true로 변경
      return (
        tempCharacterNumber.wholeGenderNumber === '4+' ||
        (typeof tempCharacterNumber.wholeGenderNumber === 'number' && tempCharacterNumber.wholeGenderNumber > 0)
      );
    }

    if (initialCharacterNumber.isWholeGenderNumberChecked && tempCharacterNumber?.isWholeGenderNumberChecked) {
      // 케이스 (3): 마지막 state와 현재 모두 true
      return tempCharacterNumber.wholeGenderNumber !== initialCharacterNumber.wholeGenderNumber;
    }

    if (initialCharacterNumber.isWholeGenderNumberChecked && !tempCharacterNumber?.isWholeGenderNumberChecked) {
      // 케이스 (4): true에서 false로 변경
      return (
        tempCharacterNumber?.maleNumber === '4+' ||
        tempCharacterNumber?.femaleNumber === '4+' ||
        (typeof tempCharacterNumber?.maleNumber === 'number' &&
          typeof tempCharacterNumber?.femaleNumber === 'number' &&
          tempCharacterNumber?.maleNumber + tempCharacterNumber?.femaleNumber >= 1)
      );
    }

    return false;
  };

  const handleApplyClick = () => {
    const newText = tempCharacterNumber?.isWholeGenderNumberChecked
      ? `성별 무관 ${tempCharacterNumber?.wholeGenderNumber}명`
      : `남자 ${tempCharacterNumber?.maleNumber}명 ∙ 여자 ${tempCharacterNumber?.femaleNumber}명`;
    setDisplayText(newText);
    setIsSelectPaperOpen(false);

    setInitialCharacterNumber({
      maleNumber: tempCharacterNumber?.maleNumber ?? 0,
      femaleNumber: tempCharacterNumber?.femaleNumber ?? 0,
      wholeGenderNumber: tempCharacterNumber?.wholeGenderNumber ?? 0,
      isWholeGenderNumberChecked: tempCharacterNumber?.isWholeGenderNumberChecked ?? false
    });

    // 적용 버튼 클릭 시 현재 값들을 상위 컴포넌트로 전달
    onApplyClick({
      maleNumber: tempCharacterNumber?.maleNumber ?? 0,
      femaleNumber: tempCharacterNumber?.femaleNumber ?? 0,
      wholeGenderNumber: tempCharacterNumber?.wholeGenderNumber,
      isWholeGenderNumberChecked: tempCharacterNumber?.isWholeGenderNumberChecked
    });
  };

  const handleChipXButtonClick = () => {
    setDisplayText(null);
    setIsSelectPaperOpen(false);

    setInitialCharacterNumber({
      maleNumber: characterNumber?.maleNumber ?? 0,
      femaleNumber: characterNumber?.femaleNumber ?? 0,
      wholeGenderNumber: characterNumber?.wholeGenderNumber ?? 0,
      isWholeGenderNumberChecked: characterNumber?.isWholeGenderNumberChecked ?? false
    });

    setTempCharacterNumber({
      maleNumber: characterNumber?.maleNumber ?? 0,
      femaleNumber: characterNumber?.femaleNumber ?? 0,
      wholeGenderNumber: characterNumber?.wholeGenderNumber ?? 0,
      isWholeGenderNumberChecked: characterNumber?.isWholeGenderNumberChecked ?? false
    });

    if (onChipXButtonClick) {
      onChipXButtonClick();
    }
  };

  const NumberControl = ({
    value,
    options,
    onIncrease,
    onDecrease,
    disabled
  }: {
    value: number | string;
    options: (number | string)[];
    onIncrease: () => void;
    onDecrease: () => void;
    disabled?: boolean;
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colorSet.gray100,
        borderRadius: 20,
        padding: '4px'
      }}
    >
      <img
        onClick={onDecrease}
        src={disabled || options.indexOf(value) === 0 ? minus_circle_disabled : minus_circle_enabled}
        alt=""
        style={{ width: 32, height: 32, cursor: disabled || options.indexOf(value) === 0 ? 'default' : 'pointer' }}
      />

      <span style={{ width: '40px', textAlign: 'center' }}>
        <Text variant="subtitle" fontWeight="bold" color={disabled ? colorSet.gray300 : colorSet.gray600}>
          {value}
        </Text>
      </span>

      <img
        onClick={onIncrease}
        src={disabled || options.indexOf(value) === options.length - 1 ? plus_circle_disabled : plus_circle_enabled}
        alt=""
        style={{
          width: 32,
          height: 32,
          cursor: disabled || options.indexOf(value) === options.length - 1 ? 'default' : 'pointer'
        }}
      />
    </div>
  );

  // 기존 값으로 돌아가기 (적용 전 Paper 닫을 시 실행)
  const resetToLastSavedValues = () => {
    setTempCharacterNumber(initialCharacterNumber);
  };

  // Paper 닫기
  const handleClose = () => {
    setIsSelectPaperOpen(false);
    resetToLastSavedValues();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const selectPaper = document.querySelector('.selectPaper');
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        selectPaper &&
        !selectPaper.contains(event.target as Node)
      ) {
        setIsSelectPaperOpen(false);
        resetToLastSavedValues();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [initialCharacterNumber]);

  // characterNumber prop value가 업데이트될 시 displayText, initialCharacterNumber, tempCharacterNumber setState
  useEffect(() => {
    if (!characterNumber) {
      setDisplayText(null);
      setInitialCharacterNumber({
        maleNumber: 0,
        femaleNumber: 0,
        isWholeGenderNumberChecked: false,
        wholeGenderNumber: 0
      });
      setTempCharacterNumber({
        maleNumber: 0,
        femaleNumber: 0,
        isWholeGenderNumberChecked: false,
        wholeGenderNumber: 0
      });
    } else {
      setDisplayText(
        characterNumber.isWholeGenderNumberChecked
          ? `성별무관 ${characterNumber.wholeGenderNumber}명`
          : `남자 ${characterNumber.maleNumber}명 ∙ 여자 ${characterNumber.femaleNumber}명`
      );
      setInitialCharacterNumber({
        maleNumber: characterNumber.maleNumber ?? 0,
        femaleNumber: characterNumber.femaleNumber ?? 0,
        wholeGenderNumber: characterNumber.wholeGenderNumber ?? 0,
        isWholeGenderNumberChecked: Boolean(characterNumber.isWholeGenderNumberChecked)
      });
      setTempCharacterNumber({
        maleNumber: characterNumber.maleNumber ?? 0,
        femaleNumber: characterNumber.femaleNumber ?? 0,
        wholeGenderNumber: characterNumber.wholeGenderNumber ?? 0,
        isWholeGenderNumberChecked: Boolean(characterNumber.isWholeGenderNumberChecked)
      });
    }
  }, [characterNumber]);

  return (
    <>
      <div className="characterNumberSelect" style={{ display: 'inline-flex', position: 'relative' }} ref={selectRef}>
        {type === CharacterNumberInputType.SELECT ? (
          <div
            onClick={() => !disabled && setIsSelectPaperOpen(!isSelectPaperOpen)}
            style={{
              width: 233,
              height: 44,
              border: `1px solid ${colorSet.gray300}`,
              borderRadius: 4,
              cursor: disabled ? 'default' : 'pointer',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0px 16px',
              backgroundColor: disabled ? colorSet.gray100 : colorSet.white
            }}
          >
            {displayText ? <Text variant="body1">{displayText}</Text> : null}
            <ChevronDownGray400Icon
              style={{
                width: 20,
                height: 20
              }}
            />
          </div>
        ) : null}

        {type === CharacterNumberInputType.CHIP ? (
          <div style={{ position: 'relative', display: 'inline-flex', whiteSpace: 'nowrap' }}>
            {!displayText ? (
              <div
                style={{
                  height: 38,
                  padding: '9px 16px',
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '12px',
                  border: `1px solid ${isSelectPaperOpen ? colorSet.primaryMain : isError ? colorSet.errorMain : colorSet.gray300}`,
                  backgroundColor: disabled
                    ? colorSet.gray200
                    : isSelectPaperOpen
                      ? 'rgba(119, 78, 255, 0.15)'
                      : 'transparent',
                  cursor: disabled ? 'default' : 'pointer'
                }}
                onClick={() => !disabled && setIsSelectPaperOpen(!isSelectPaperOpen)}
              >
                {needTooltip && <Tooltip text={tooltipText} />}
                <Text variant="body1" color={disabled ? colorSet.gray400 : colorSet.gray600}>
                  인원수
                </Text>
              </div>
            ) : (
              <div
                style={{
                  height: 38,
                  padding: '9px 16px',
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '12px',
                  border: `1px solid ${isSelectPaperOpen ? colorSet.primaryMain : colorSet.gray600}`,
                  backgroundColor: isSelectPaperOpen ? 'rgba(119, 78, 255, 0.15)' : colorSet.gray200,
                  cursor: 'pointer'
                }}
                onClick={() => !disabled && setIsSelectPaperOpen(!isSelectPaperOpen)}
              >
                {needTooltip && <Tooltip text={tooltipText} />}
                <Text variant="body1" fontWeight="bold" style={{ marginRight: 8 }}>
                  인원수
                </Text>
                {displayText ? <Text variant="body1">{displayText}</Text> : null}
                <ChipXButton onClick={handleChipXButtonClick} />
              </div>
            )}
          </div>
        ) : null}

        <Popover
          open={isSelectPaperOpen && !disabled}
          anchorEl={selectRef.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              onMouseDown: (event) => {
                event.stopPropagation();
              },
              sx: {
                marginTop: type === CharacterNumberInputType.CHIP ? '10px' : '0px',
                width: '274px',
                padding: '12px 16px',
                backgroundColor: colorSet.white,
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                zIndex: styleConstant.zIndexChipPaper,
                maxWidth: 'calc(100vw - 16px)',
                maxHeight: 'calc(100vh - 16px)'
              }
            }
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              marginBottom: '12px'
            }}
          >
            {isWholeGenderNumberOnly ? null : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ width: 100, display: 'flex', justifyContent: 'center' }}>
                    <Text variant="body1">남자</Text>
                  </div>
                  <NumberControl
                    value={tempCharacterNumber?.maleNumber ?? 0}
                    options={maleNumberOptions}
                    onIncrease={() => handleNumberChange('male', 'increase', maleNumberOptions)}
                    onDecrease={() => handleNumberChange('male', 'decrease', maleNumberOptions)}
                    disabled={tempCharacterNumber?.isWholeGenderNumberChecked}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ width: 100, display: 'flex', justifyContent: 'center' }}>
                    <Text variant="body1">여자</Text>
                  </div>
                  <NumberControl
                    value={tempCharacterNumber?.femaleNumber ?? 0}
                    options={femaleNumberOptions}
                    onIncrease={() => handleNumberChange('female', 'increase', femaleNumberOptions)}
                    onDecrease={() => handleNumberChange('female', 'decrease', femaleNumberOptions)}
                    disabled={tempCharacterNumber?.isWholeGenderNumberChecked}
                  />
                </div>
              </div>
            )}

            {isGenderNumbersOnly ? null : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div
                  style={{
                    width: 100,
                    display: 'flex',
                    justifyContent: isWholeGenderNumberOnly ? 'center' : 'flex-start',
                    alignItems: 'center'
                  }}
                >
                  {isWholeGenderNumberOnly ? null : (
                    <Checkbox
                      checked={Boolean(tempCharacterNumber?.isWholeGenderNumberChecked)}
                      onChange={(e) => {
                        setTempCharacterNumber({
                          ...tempCharacterNumber,
                          isWholeGenderNumberChecked: e.target.checked
                        });
                      }}
                    />
                  )}

                  <Text variant="body1">성별 무관</Text>
                </div>
                <NumberControl
                  value={tempCharacterNumber?.wholeGenderNumber ?? 0}
                  options={wholeGenderOptions}
                  onIncrease={() => handleNumberChange('whole', 'increase', wholeGenderOptions)}
                  onDecrease={() => handleNumberChange('whole', 'decrease', wholeGenderOptions)}
                  disabled={!tempCharacterNumber?.isWholeGenderNumberChecked}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              disabled={!isApplyEnabled()}
              variant="contained"
              onClick={() => {
                setIsSelectPaperOpen(false);
                handleApplyClick();
              }}
              sx={{ width: 90, height: 44, borderRadius: 22 }}
            >
              적용
            </Button>
          </div>
        </Popover>
      </div>
    </>
  );
};
